// 261010 Epic 2 / P6 — CHỦ DỮ LIỆU `GET step-reviews?eventId=` của một video, tách khỏi thẻ để MỘT nơi sở hữu (nạp + poll + chấm + phân xử) rồi chia cho nhiều chỗ dùng.
// Lý do: bước Video cần dữ liệu này ở HAI nơi (thẻ "AI chấm clip · Bản N" trên đầu trang + tag / nút trên từng dòng bản clip) — hai bản poll song song là sai.
// Logic y hệt phần trước đây nằm trong `WorkflowStepReviewCard` (P3d/P5c/P5d) — chỉ dời chỗ; thẻ tự dựng hook cho chính nó khi không được truyền `controller`.
import { useCallback, useEffect, useRef, useState } from 'react';
import { filmApi } from '../../services/api/film';
import { useAliveRef } from '../../hooks/useAliveRef';
import type { LeaderDecision, StepReviewKey, StepReviewsResponse } from '../../types/filmStepReview';
import { apiErrorText } from './apiErrorText';
import { createStepReviewPoller, type StepReviewPoller } from './stepReviewPoller';
import { stepReviewActionError, type StepReviewError } from './stepReviewPolicy';

export interface StepReviewsController {
  view: StepReviewsResponse | null;
  loadError: string | null;
  pollError: boolean;
  /** Đang gửi lệnh chấm / phân xử (một việc một lúc). */
  busy: 'run' | 'decide' | null;
  /** P6: bản clip đang được gửi lệnh chấm (POST chậm vài chục giây) — để chỉ dòng / thẻ của bản đó hiện spinner. null khi không chấm clip hoặc rảnh. */
  busyClipId: string | null;
  actionError: StepReviewError | null;
  /** Nạp lại một lượt (lỗi ⇒ `loadError`). */
  load: () => Promise<void>;
  /** Nút "Tải lại" của dòng "không cập nhật được trạng thái chấm". */
  reloadPolling: () => void;
  dismissError: () => void;
  /** Gửi chấm (202). `candidateClipId` chỉ cho `CLIP_REVIEW`. Xong (kể cả lỗi 409) luôn nạp lại + báo `onActionDone`. */
  run: (stepKey: StepReviewKey, candidateClipId?: string) => Promise<void>;
  /** Phân xử một phiếu. Trả true khi BE nhận. */
  decide: (reviewId: string, stepKey: StepReviewKey, decision: LeaderDecision, note: string) => Promise<boolean>;
}

interface Opts {
  planId: string;
  eventId: string;
  /** Sau MỌI thao tác ghi và khi phiếu chạy xong: trang tải lại `workflow-status` (alert + chuông). */
  onActionDone: () => void;
  /** false ⇒ không nạp, không poll (hook vẫn phải được gọi — luật hook). Mặc định true. */
  enabled?: boolean;
}

export function useStepReviews({ planId, eventId, onActionDone, enabled = true }: Opts): StepReviewsController {
  const alive = useAliveRef();
  const [view, setView] = useState<StepReviewsResponse | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [pollError, setPollError] = useState(false);
  const [busy, setBusy] = useState<'run' | 'decide' | null>(null);
  const [busyClipId, setBusyClipId] = useState<string | null>(null);
  const [actionError, setActionError] = useState<StepReviewError | null>(null);
  const poller = useRef<StepReviewPoller | null>(null);
  const keyRef = useRef(`${planId}:${eventId}`);
  keyRef.current = `${planId}:${eventId}`;
  const doneRef = useRef(onActionDone);
  doneRef.current = onActionDone;

  const apply = useCallback((r: StepReviewsResponse) => {
    if (!alive.current) return;
    setView(r); setLoadError(null);
    poller.current?.sync(r);
    setPollError(!!poller.current?.isErrored()); // poll chết vì lỗi mạng thì dòng báo còn tới khi bấm Tải lại — sau thao tác ghi cũng không xoá
  }, [alive]);

  const load = useCallback(async () => {
    const key = `${planId}:${eventId}`;
    try { const r = await filmApi.listStepReviews(planId, eventId); if (keyRef.current === key) apply(r); }
    catch (e) { if (alive.current && keyRef.current === key) setLoadError(apiErrorText(e, 'Tải phiếu AI chấm')); }
  }, [planId, eventId, apply, alive]);

  useEffect(() => {
    setView(null); setLoadError(null); setPollError(false); setBusy(null); setBusyClipId(null); setActionError(null);
    if (!enabled) return undefined;
    const p = createStepReviewPoller({
      fetchReviews: () => filmApi.listStepReviews(planId, eventId),
      onData: (r) => { setView(r); setPollError(false); },
      onFinished: () => { doneRef.current(); },
      onError: () => setPollError(true),
    });
    poller.current = p;
    void load();
    return () => { p.stop(); poller.current = null; };
  }, [planId, eventId, enabled, load]);

  const run = useCallback(async (stepKey: StepReviewKey, candidateClipId?: string) => {
    setBusy('run'); setBusyClipId(candidateClipId ?? null); setActionError(null);
    try {
      if (candidateClipId) await filmApi.startStepReview(planId, eventId, stepKey, candidateClipId);
      else await filmApi.startStepReview(planId, eventId, stepKey);
    } catch (e) { setActionError(stepReviewActionError(e, 'AI chấm', stepKey)); }
    await load(); // phiếu RUNNING đã có ngay sau 202 (hàng giữ chỗ ghi trước khi enqueue); lỗi 409 cũng nạp lại để nút khớp trạng thái thật
    if (alive.current) { setBusy(null); setBusyClipId(null); }
    doneRef.current();
  }, [planId, eventId, load, alive]);

  const decide = useCallback(async (reviewId: string, stepKey: StepReviewKey, decision: LeaderDecision, note: string): Promise<boolean> => {
    setBusy('decide'); setActionError(null);
    let ok = false;
    try { await filmApi.decideStepReview(reviewId, { decision, note: note.trim() }); ok = true; }
    catch (e) { setActionError(stepReviewActionError(e, 'Phân xử phiếu', stepKey)); }
    await load();
    if (alive.current) setBusy(null);
    doneRef.current();
    return ok;
  }, [load, alive]);

  const reloadPolling = useCallback(() => { void poller.current?.reload(); }, []);
  const dismissError = useCallback(() => setActionError(null), []);

  return { view, loadError, pollError, busy, busyClipId, actionError, load, reloadPolling, dismissError, run, decide };
}
